// src/pages/admin/CompanyDashboard.js

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardProvider, useDashboard } from '../../contexts/DashboardContext';
import { VacationProvider } from '../../contexts/VacationContext';
import ActiveSessionsWidget from '../../components/dashboard/ActiveSessionsWidget';
import ActivityFeed from '../../components/dashboard/ActivityFeed';
import DashboardAlerts from '../../components/dashboard/DashboardAlerts';
import VacationWidget from '../../components/dashboard/VacationWidget';
import VacationMetricCard from '../../components/dashboard/VacationMetricCard';
import MetricCard from '../../components/dashboard/MetricCard';
import HoursBalanceSummaryBar from '../../components/dashboard/HoursBalanceSummaryBar';
import HoursBalanceOutliersWidget from '../../components/dashboard/HoursBalanceOutliersWidget';
import {
  RiBuilding2Line,
  RiTeamLine,
  RiTimerLine
} from 'react-icons/ri';
import '../../styles/dashboard.css';

// Dashboard Content Component (uses dashboard context)
function DashboardContent() {
  const { t } = useTranslation(['dashboard', 'vacation']);
  const { currentUser, currentCompany, userRole } = useAuth();
  const {
    metrics,
    metricsLoading,
    activeTab,
    setActiveTab,
    recentActivity,
    alerts
  } = useDashboard();

  const [activeWidget, setActiveWidget] = useState('active');

  const isOwner = userRole === 'owner';

  const handleTabChange = (tab) => {
    setActiveWidget(tab);
    setActiveTab(tab);
  };

  // Status pill: green "Alle Systeme aktiv" unless a critical alert exists.
  // Derived from the already-loaded `alerts` array — no new fetch.
  const criticalAlertsCount = alerts.filter(
    (alert) => alert.severity === 'critical' || alert.severity === 'error'
  ).length;
  const systemsActive = criticalAlertsCount === 0;

  // Header subtitle: "{name} · {email} · {N} Standorte"
  const companyName =
    currentCompany?.name || currentUser?.displayName || t('dashboard:notAvailable');
  const companyEmail =
    currentCompany?.email || currentUser?.email || t('dashboard:notAvailable');

  return (
    <div className="dashboard-container">
      {/* Header row: title + subtitle (left) / status pill (right) */}
      <div className="dashboard-header">
        <div className="dashboard-header-text">
          <h1>{t('dashboard:title')}</h1>
          <p className="dashboard-subtitle">
            {companyName} · {companyEmail} ·{' '}
            {metricsLoading ? '…' : metrics.departments} {t('dashboard:locations')}
          </p>
        </div>

        <div
          className={`status-pill ${systemsActive ? 'status-pill--active' : 'status-pill--alert'}`}
        >
          <span className="status-dot" />
          {systemsActive ? t('dashboard:allSystemsActive') : t('dashboard:systemAlerts')}
        </div>
      </div>

      {/* 4 metric cards */}
      <div className="dashboard-stats">
        <MetricCard
          icon={<RiBuilding2Line />}
          label={t('dashboard:departments')}
          value={metrics.departments}
          loading={metricsLoading}
        />

        <MetricCard
          icon={<RiTeamLine />}
          label={t('dashboard:staffMembers')}
          value={metrics.staffMembers}
          loading={metricsLoading}
        />

        <MetricCard
          icon={<RiTimerLine />}
          label={t('dashboard:activeSessions')}
          value={metrics.activeSessions}
          sublabel={t('dashboard:now')}
          loading={metricsLoading}
        />

        {/* Vacation-today card - Owner Only (same gate as before) */}
        {isOwner && (
          <VacationMetricCard
            staffTotal={metrics.staffMembers}
            totalLoading={metricsLoading}
          />
        )}
      </div>

      {/* Company-wide Stundensaldo-Übersicht summary bar - Owner Only */}
      {isOwner && <HoursBalanceSummaryBar />}

      {/* Tabbed widget panel */}
      <div className="dashboard-widgets">

        {/* CHANGE: .widget-tabs-wrapper is required for the right-edge
            gradient fade defined in dashboard-widgets.css to work.
            The ::after pseudo-element on .widget-tabs-wrapper overlays
            the tab bar from outside the overflow-x: auto clipping
            context of .widget-tabs, making the gradient visible.     */}
        <div className="widget-tabs-wrapper">
          <div className="widget-tabs">
            <button
              className={`tab-button ${activeWidget === 'active' ? 'active' : ''}`}
              onClick={() => handleTabChange('active')}
            >
              {t('dashboard:currentlyClockedIn')}
              {!metricsLoading && metrics.activeSessions > 0 && (
                <span className="tab-badge">{metrics.activeSessions}</span>
              )}
            </button>

            <button
              className={`tab-button ${activeWidget === 'activity' ? 'active' : ''}`}
              onClick={() => handleTabChange('activity')}
            >
              {t('dashboard:recentActivity')}
              {recentActivity.length > 0 && (
                <span className="tab-badge">{recentActivity.length}</span>
              )}
            </button>

            {/* Vacation Tab - Owner Only */}
            {isOwner && (
              <button
                className={`tab-button ${activeWidget === 'vacation' ? 'active' : ''}`}
                onClick={() => handleTabChange('vacation')}
              >
                {t('vacation:tabTitle')}
              </button>
            )}

            {/* Hours Balance Outliers Tab - Owner Only */}
            {isOwner && (
              <button
                className={`tab-button ${activeWidget === 'hoursBalance' ? 'active' : ''}`}
                onClick={() => handleTabChange('hoursBalance')}
              >
                {t('dashboard:hoursBalanceOutliers.tabTitle')}
              </button>
            )}

            <button
              className={`tab-button ${activeWidget === 'alerts' ? 'active' : ''}`}
              onClick={() => handleTabChange('alerts')}
            >
              {t('dashboard:systemAlerts')}
              {alerts.length > 0 && (
                <span className="tab-badge">{alerts.length}</span>
              )}
            </button>
          </div>
        </div>

        <div className="widget-content">
          {activeWidget === 'active'       && <ActiveSessionsWidget />}
          {activeWidget === 'activity'     && <ActivityFeed />}
          {activeWidget === 'vacation'     && isOwner && <VacationWidget />}
          {activeWidget === 'hoursBalance' && isOwner && <HoursBalanceOutliersWidget />}
          {activeWidget === 'alerts'       && <DashboardAlerts />}
        </div>

      </div>
    </div>
  );
}

// Main Component (provides dashboard context)
function CompanyDashboard() {
  return (
    <DashboardProvider>
      <VacationProvider>
        <DashboardContent />
      </VacationProvider>
    </DashboardProvider>
  );
}

export default CompanyDashboard;